/**
 * Web Storage 셧다운 환경용 인메모리 shim (LOCARORA-WEB-6C / 6D).
 *
 * ## 왜 필요한가
 * iOS Safari에서 "모든 쿠키 차단"이 켜져 있거나 프라이빗/임베디드 컨텍스트로
 * 실행되면, `window.sessionStorage` / `window.localStorage`는 값을 돌려주는 대신
 * **프로퍼티 접근 자체가** `SecurityError: The operation is insecure.`
 * (DOMException code 18)를 던진다. try/catch 없는 단순 참조 한 줄이 그대로
 * 런타임 에러가 된다는 뜻이다.
 *
 * 실제로 `@supabase/realtime-js`가 `createBrowserClient()` 안에서 Phoenix Socket을
 * 생성하며 `opts.sessionStorage || global && global.sessionStorage`를 읽는데
 * (node_modules/@supabase/phoenix/priv/static/phoenix.mjs), realtime-js의
 * `_initializeOptions`가 sessionStorage 옵션을 통과시켜 주지 않아 **우리가 주입할
 * 방법이 없다.** 그 결과 해당 사용자에게는 `createClient()` 호출이 전부 throw 하고,
 * 렌더 중 호출하는 곳(VerificationGuideWrapper) 때문에 사이트 전체가 에러 바운더리로
 * 떨어진다.
 *
 * ## 왜 call site 방어가 아니라 shim인가
 * 앱 전역에 raw `sessionStorage.` / `localStorage.` 접근이 ~90곳(25파일) 있고,
 * 위 Phoenix처럼 **우리가 손댈 수 없는 node_modules 코드**도 같은 전역을 읽는다.
 * 90곳을 guard 해도 서드파티는 여전히 죽는다. 그래서 진입점에서 전역 한 쌍만
 * 바꿔치기하는 단일 지점 fix를 택한다.
 *
 * ## 한계(수용됨)
 * shim은 메모리에만 저장하므로 **새로고침하면 전부 사라진다.** 애초에 이 모드의
 * 사용자는 Supabase auth 쿠키조차 보관할 수 없어 세션 유지가 불가능하므로,
 * "저장은 안 되지만 사이트는 동작한다"가 "사이트가 아예 안 뜬다"보다 낫다.
 *
 * Window의 `localStorage`/`sessionStorage`는 [Global] 인터페이스의 **configurable
 * own accessor**라서, 같은 이름의 own data property로 덮어쓰는 방식이 Safari에서
 * 동작한다. defineProperty 자체가 막히면 조용히 포기한다(할 수 있는 게 없음).
 */

const MEMORY_FLAG = "__locaroraMemoryStorage";
const PROBE_KEY = "__probe__";

type StorageName = "localStorage" | "sessionStorage";

export type StorageShimResult = {
  localStorage: boolean;
  sessionStorage: boolean;
};

type WindowStorageSlots = Record<StorageName, Storage | null | undefined>;

function isMemoryShim(value: unknown): boolean {
  try {
    return Boolean(
      value && (value as Record<string, unknown>)[MEMORY_FLAG] === true,
    );
  } catch {
    return false;
  }
}

/** 실제 Storage API와 같은 형태(문자열 강제 변환 포함)의 인메모리 구현. */
function createMemoryStorage(): Storage {
  const entries = new Map<string, string>();

  const storage: Storage = {
    get length(): number {
      return entries.size;
    },
    clear(): void {
      entries.clear();
    },
    getItem(key: string): string | null {
      const k = String(key);
      return entries.has(k) ? (entries.get(k) as string) : null;
    },
    key(index: number): string | null {
      const i = Number(index);
      if (!Number.isFinite(i) || i < 0) return null;
      const keys = Array.from(entries.keys());
      return i < keys.length ? keys[Math.trunc(i)] : null;
    },
    removeItem(key: string): void {
      entries.delete(String(key));
    },
    setItem(key: string, value: string): void {
      entries.set(String(key), String(value));
    },
  };

  Object.defineProperty(storage, MEMORY_FLAG, {
    value: true,
    enumerable: false,
    configurable: false,
    writable: false,
  });

  return storage;
}

type ProbeResult = "usable" | "shimmed" | "unusable";

function probe(name: StorageName): ProbeResult {
  let value: Storage | null | undefined;
  try {
    // 이 한 줄이 Safari에서 SecurityError를 던지는 지점이다.
    value = (window as unknown as WindowStorageSlots)[name];
  } catch {
    return "unusable";
  }

  if (value === null || value === undefined) return "unusable";
  if (isMemoryShim(value)) return "shimmed";

  try {
    value.setItem(PROBE_KEY, "1");
    value.removeItem(PROBE_KEY);
  } catch {
    // 읽기는 되지만 쓰기가 막힌 경우(구형 Safari 프라이빗 모드의 QuotaExceededError 등).
    return "unusable";
  }

  return "usable";
}

function installOne(name: StorageName): boolean {
  const state = probe(name);
  if (state === "shimmed") return true; // 이미 설치됨 — 절대 교체하지 않는다(멱등).
  if (state === "usable") return false;

  try {
    Object.defineProperty(window, name, {
      value: createMemoryStorage(),
      configurable: true,
      enumerable: true,
      writable: false,
    });
  } catch {
    return false;
  }

  return true;
}

/**
 * 사용 불가능한 `window.localStorage` / `window.sessionStorage`를 인메모리
 * 구현으로 교체한다. 멱등이며, SSR(=`window` 없음)에서는 아무것도 하지 않는다.
 *
 * @returns 각 전역에 대해 shim이 설치되어 있는지 여부(true = 메모리 저장소 사용 중).
 */
export function installWebStorageShims(): StorageShimResult {
  if (typeof window === "undefined") {
    return { localStorage: false, sessionStorage: false };
  }

  return {
    localStorage: installOne("localStorage"),
    sessionStorage: installOne("sessionStorage"),
  };
}
